import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Input, Button, Space, Table, Modal, Form, DatePicker,
  Tooltip, Typography, Select, Progress, Switch, Alert, Avatar,
  TimePicker, Divider, InputNumber, Spin
} from "antd";
import { useDispatch } from "react-redux";
import axios from "axios";
import moment from "moment";
import toast from "react-hot-toast";
import Layout from "../../components/layout";
import { ruleToMatcher } from "../../components/utils/clinicDay";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  SearchOutlined, FileExcelOutlined, FilePdfOutlined, FilterOutlined,
  ClearOutlined, CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined,
  WarningOutlined, InfoCircleOutlined, ClockCircleOutlined, SettingOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

/* ─── Design tokens ───────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";
const PURPLE     = "#0d9488";
const PURPLE_LIGHT = "#f8fafc";

/* ─── Status config ──────────────────────────────────────────── */
const STATUS_CONFIG = {
  pending:    { color: "#b45309", bg: "#fef9c3",   progress: 30,  bar: "#faad14" },
  approved:   { color: EMERALD,   bg: TEAL_LIGHT,  progress: 60,  bar: TEAL      },
  "follow-up":{ color: PURPLE,    bg: PURPLE_LIGHT,progress: 90,  bar: "#22c55e" },
  completed:  { color: "#16a34a", bg: "#dcfce7",   progress: 100, bar: "#22c55e" },
  rejected:   { color: "#dc2626", bg: "#fee2e2",   progress: 0,   bar: "#ef4444" },
};
const getStatusCfg      = (s) => STATUS_CONFIG[s] || { color: MUTED, bg: SURFACE, progress: 0, bar: "#d9d9d9" };
const getStatusProgress = (s) => getStatusCfg(s).progress;

/* ─── Time utilities ─────────────────────────────────────────── */
const toMins = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const fromMins = (mins) =>
  `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

const fmt12 = (t) => {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  const p = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${p}`;
};

/**
 * Generate slots between start and end "HH:mm" with given interval (minutes).
 * (Kept for preview in settings modal; not used for appointment slots anymore)
 */
function generateSlots(start, end, intervalMin) {
  if (!start || !end || !intervalMin) return [];
  const slots = [];
  let cur = toMins(start);
  const endM = toMins(end);
  while (cur < endM) {
    slots.push(fromMins(cur));
    cur += intervalMin;
  }
  return slots;
}

/* ─── Style injection (unchanged) ───────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("appt-admin-styles")) return;
  const el = document.createElement("style");
  el.id = "appt-admin-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .aa-root { font-family:'DM Sans',sans-serif; background:${SURFACE}; min-height:100vh; }
    .aa-wrap { max-width:1280px; margin:0 auto; padding:32px 24px 64px; }

    /* ── Hero ── */
    .aa-hero {
      background:linear-gradient(135deg,${SLATE} 0%,${SLATE_MID} 100%);
      border-radius:24px; padding:36px 44px;
      position:relative; overflow:hidden; margin-bottom:28px;
    }
    .aa-hero::before {
      content:''; position:absolute; top:-80px; right:-80px;
      width:300px; height:300px; border-radius:50%;
      background:radial-gradient(circle,${TEAL}44 0%,transparent 70%);
      filter:blur(32px); pointer-events:none;
    }
    .aa-hero::after {
      content:''; position:absolute; bottom:-60px; left:-40px;
      width:200px; height:200px; border-radius:50%;
      background:radial-gradient(circle,${EMERALD}33 0%,transparent 70%);
      filter:blur(26px); pointer-events:none;
    }
    .aa-eyebrow {
      display:inline-block; padding:4px 12px; border-radius:20px;
      background:${TEAL_LIGHT}; color:${EMERALD};
      font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase;
      font-family:'Sora',sans-serif; margin-bottom:12px;
    }
    .aa-hero-badges { display:flex; gap:8px; flex-wrap:wrap; margin-top:12px; }
    .aa-hero-badge {
      background:rgba(255,255,255,0.12); border-radius:20px;
      padding:4px 12px; font-size:12px; color:rgba(255,255,255,0.75);
    }

    /* ── Toolbar ── */
    .aa-toolbar {
      background:${WHITE}; border-radius:16px;
      box-shadow:0 4px 20px rgba(0,0,0,.06);
      padding:16px 20px; margin-bottom:20px;
      display:flex; align-items:center; gap:12px; flex-wrap:wrap;
    }

    /* ── Inputs ── */
    .aa-root .aa-search.ant-input-affix-wrapper {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important; flex:1; min-width:200px;
      font-size:13px !important; transition:border-color .2s,box-shadow .2s !important;
    }
    .aa-root .aa-search.ant-input-affix-wrapper:hover,
    .aa-root .aa-search.ant-input-affix-wrapper-focused {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .aa-root .aa-status-select .ant-select-selector {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important;
      display:flex !important; align-items:center !important;
      font-size:13px !important; transition:border-color .2s,box-shadow .2s !important;
    }
    .aa-root .aa-status-select .ant-select-selection-item,
    .aa-root .aa-status-select .ant-select-selection-placeholder { line-height:38px !important; }
    .aa-root .aa-status-select.ant-select-focused .ant-select-selector,
    .aa-root .aa-status-select .ant-select-selector:hover {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }

    /* ── Toolbar buttons ── */
    .aa-btn {
      height:40px !important; padding:0 16px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:600 !important;
      border:1.5px solid #e2e8f0 !important; background:${WHITE} !important;
      color:${SLATE_MID} !important; transition:border-color .2s,color .2s !important;
      display:flex !important; align-items:center !important; gap:6px !important;
    }
    .aa-btn:hover           { border-color:${TEAL}    !important; color:${TEAL}    !important; }
    .aa-btn-excel:hover     { border-color:#16a34a    !important; color:#16a34a    !important; }
    .aa-btn-pdf:hover       { border-color:#dc2626    !important; color:#dc2626    !important; }
    .aa-btn-settings:hover  { border-color:${PURPLE}  !important; color:${PURPLE}  !important; }

    /* ── Table ── */
    .aa-table-wrap {
      background:${WHITE}; border-radius:20px;
      box-shadow:0 4px 20px rgba(0,0,0,.06); overflow:hidden;
    }
    .aa-root .ant-table-thead > tr > th {
      background:${SURFACE} !important; color:${SLATE_MID} !important;
      font-size:11px !important; font-weight:700 !important;
      letter-spacing:.5px; text-transform:uppercase;
      border-bottom:1.5px solid #f1f5f9 !important;
      font-family:'Sora',sans-serif !important; padding:14px 16px !important;
    }
    .aa-root .ant-table-tbody > tr > td {
      font-size:13px !important; color:${SLATE_MID} !important;
      padding:12px 16px !important; border-bottom:1px solid #f8fafc !important;
    }
    .aa-root .ant-table-tbody > tr:hover > td { background:#f0fdfb !important; }
    .aa-root .ant-pagination { padding:16px !important; }
    .aa-root .ant-pagination-item-active { border-color:${TEAL} !important; }
    .aa-root .ant-pagination-item-active a { color:${TEAL} !important; }

    /* ── Badges ── */
    .aa-badge {
      padding:3px 10px; border-radius:20px;
      font-size:11px; font-weight:700; font-family:'Sora',sans-serif; display:inline-block;
    }

    /* ── Row action buttons ── */
    .aa-approve-btn {
      height:30px !important; padding:0 12px !important; border-radius:8px !important;
      font-size:12px !important; font-weight:700 !important; border:1.5px solid ${TEAL} !important;
      color:${TEAL} !important; background:${WHITE} !important; transition:all .2s !important;
    }
    .aa-approve-btn:hover { background:${TEAL} !important; color:${WHITE} !important; }
    .aa-reject-btn {
      height:30px !important; padding:0 12px !important; border-radius:8px !important;
      font-size:12px !important; font-weight:700 !important; border:1.5px solid #ef4444 !important;
      color:#ef4444 !important; background:${WHITE} !important; transition:all .2s !important;
    }
    .aa-reject-btn:hover { background:#ef4444 !important; color:${WHITE} !important; }

    /* ── Approve/Reject Modal ── */
    .aa-modal .ant-modal-content { border-radius:20px !important; overflow:hidden; padding:0 !important; }
    .aa-modal .ant-modal-header  { padding:20px 28px !important; border-radius:0 !important; margin:0 !important; }
    .aa-modal.approve-modal .ant-modal-header { background:linear-gradient(135deg,${SLATE},${SLATE_MID}) !important; }
    .aa-modal.reject-modal  .ant-modal-header { background:linear-gradient(135deg,#7f1d1d,#dc2626) !important; }
    .aa-modal .ant-modal-title {
      color:${WHITE} !important; font-family:'Sora',sans-serif !important;
      font-weight:700 !important; font-size:16px !important;
    }
    .aa-modal .ant-modal-close { color:rgba(255,255,255,.5) !important; top:18px !important; }
    .aa-modal .ant-modal-close:hover { color:${WHITE} !important; }
    .aa-modal .ant-modal-body   { padding:24px 28px !important; }
    .aa-modal .ant-modal-footer { padding:16px 28px !important; border-top:1.5px solid #f1f5f9 !important; }

    .aa-modal .ant-form-item { margin-bottom:16px !important; }
    .aa-modal .ant-form-item-label { padding-bottom:4px !important; }
    .aa-modal .ant-form-item-label > label {
      font-size:12px !important; font-weight:600 !important; color:${SLATE_MID} !important; height:20px !important;
    }
    .aa-modal textarea.ant-input {
      border-radius:10px !important; border:1.5px solid #e2e8f0 !important;
      font-size:13px !important; padding:10px 12px !important; transition:border-color .2s !important;
    }
    .aa-modal textarea.ant-input:hover,
    .aa-modal textarea.ant-input:focus {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .aa-modal .ant-picker {
      border-radius:10px !important; height:44px !important;
      border:1.5px solid #e2e8f0 !important; width:100% !important;
      font-size:13px !important; transition:border-color .2s !important;
    }
    .aa-modal .ant-picker:hover, .aa-modal .ant-picker-focused {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }

    /* Date mode strip */
    .aa-date-mode-strip {
      background:${SURFACE}; border:1.5px solid #f1f5f9; border-radius:12px;
      padding:14px 16px; margin-bottom:16px;
      display:flex; align-items:center; justify-content:space-between; gap:12px;
    }
    .aa-date-mode-label { font-size:12px; font-weight:600; color:${SLATE_MID}; }
    .aa-date-mode-sub   { font-size:11px; color:${MUTED}; margin-top:2px; }

    /* ── Time slot grid ── */
    .aa-slot-grid {
      display:grid; grid-template-columns:repeat(auto-fill,minmax(82px,1fr));
      gap:8px; margin-top:4px;
    }
    .aa-slot-btn {
      height:36px; border-radius:8px; border:1.5px solid #e2e8f0;
      font-size:12px; font-weight:600; font-family:'DM Sans',sans-serif;
      cursor:pointer; background:${WHITE}; color:${SLATE_MID};
      transition:all .15s; display:flex; align-items:center; justify-content:center;
    }
    .aa-slot-btn:hover  { border-color:${TEAL}; color:${TEAL}; background:${TEAL_LIGHT}; }
    .aa-slot-btn.active { border-color:${TEAL}; color:${WHITE}; background:${TEAL}; box-shadow:0 2px 8px ${TEAL}44; }
    .aa-slot-btn:disabled,
    .aa-slot-btn[disabled] {
      opacity:.4; cursor:not-allowed; pointer-events:none;
    }
    .aa-slot-loading { text-align:center; padding:20px 0; color:${MUTED}; }

    /* ── Settings Modal ── */
    .aa-settings-modal .ant-modal-content { border-radius:20px !important; overflow:hidden; padding:0 !important; }
    .aa-settings-modal .ant-modal-header  { padding:20px 28px !important; margin:0 !important;
      background:linear-gradient(135deg,${PURPLE},#22c55e) !important; }
    .aa-settings-modal .ant-modal-title   {
      color:${WHITE} !important; font-family:'Sora',sans-serif !important; font-weight:700 !important; }
    .aa-settings-modal .ant-modal-close   { color:rgba(255,255,255,.5) !important; top:18px !important; }
    .aa-settings-modal .ant-modal-close:hover { color:${WHITE} !important; }
    .aa-settings-modal .ant-modal-body    { padding:24px 28px !important; }
    .aa-settings-modal .ant-modal-footer  { padding:16px 28px !important; border-top:1.5px solid #f1f5f9 !important; }
    .aa-interval-card {
      background:${SURFACE}; border:1.5px solid #f1f5f9; border-radius:14px; padding:18px 20px;
    }
    .aa-interval-preview {
      display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; max-height:100px; overflow:hidden;
    }
    .aa-interval-pill {
      padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600;
      background:${TEAL_LIGHT}; color:${EMERALD};
    }

    /* Modal footer buttons */
    .aa-modal-cancel {
      height:40px !important; padding:0 20px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:600 !important;
      border:1.5px solid #e2e8f0 !important; color:${SLATE_MID} !important;
    }
    .aa-modal-approve {
      height:40px !important; padding:0 24px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:700 !important; font-family:'Sora',sans-serif !important;
      background:linear-gradient(135deg,${EMERALD},${TEAL}) !important;
      border:none !important; color:${WHITE} !important; box-shadow:0 4px 12px ${TEAL}44 !important;
    }
    .aa-modal-reject {
      height:40px !important; padding:0 24px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:700 !important; font-family:'Sora',sans-serif !important;
      background:linear-gradient(135deg,#b91c1c,#dc2626) !important;
      border:none !important; color:${WHITE} !important; box-shadow:0 4px 12px #ef444444 !important;
    }
    .aa-modal-save-settings {
      height:40px !important; padding:0 24px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:700 !important; font-family:'Sora',sans-serif !important;
      background:linear-gradient(135deg,${PURPLE},#22c55e) !important;
      border:none !important; color:${WHITE} !important; box-shadow:0 4px 12px ${PURPLE}44 !important;
    }

    /* Animations */
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    .aa-fade   { animation:fadeUp .5s ease both; }
    .aa-fade-1 { animation-delay:.05s; }
    .aa-fade-2 { animation-delay:.12s; }
  `;
  document.head.appendChild(el);
};

/* ─── Main component ─────────────────────────────────────────── */
const AppointmentAdmin = () => {
  const dispatch = useDispatch();

  /* table state */
  const [appointments,   setAppointments]   = useState([]);
  const [filteredInfo,   setFilteredInfo]   = useState({});
  const [sortedInfo,     setSortedInfo]     = useState({});
  const [searchTerm,     setSearchTerm]     = useState("");
  const [statusFilter,   setStatusFilter]   = useState("All");

  /* approve/reject modal */
  const [open,           setOpen]           = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [actionStatus,   setActionStatus]   = useState("");
  const [useCustomDate,  setUseCustomDate]  = useState(false);
  const [selectedSlot,   setSelectedSlot]   = useState(null); // "HH:mm"
  const [slotsLoading,   setSlotsLoading]   = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [form] = Form.useForm();

  /* settings modal */
  const [useGlobalInterval, setUseGlobalInterval] = useState(true);
  const [settingsOpen,      setSettingsOpen]      = useState(false);
  const [timeInterval,      setTimeInterval]      = useState(30);
  const [savedInterval,     setSavedInterval]     = useState(30);
  const [settingsSaving,    setSettingsSaving]    = useState(false);
  const [settingsLoading,   setSettingsLoading]   = useState(false);
  const [effectiveInterval, setEffectiveInterval] = useState(30);


    // ── Compute display interval ──
  const displayInterval = useMemo(() => {
    if (availableSlots.length > 0) {
      return effectiveInterval;
    }
    return useGlobalInterval ? savedInterval : 30;
  }, [availableSlots, effectiveInterval, useGlobalInterval, savedInterval]);

  // Watch the schedule_date field from the form
  const scheduleDate = Form.useWatch("schedule_date", form);

  useEffect(() => { injectStyles(); }, []);

  /* ── Fetch appointments ── */
  const getAppointmentData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.get("/api/admin/get-all-appointments", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (res.data.success) setAppointments(res.data.data);
    } catch { dispatch(hideLoading()); }
  }, [dispatch]);

  useEffect(() => { getAppointmentData(); }, [getAppointmentData]);

  /* ── Fetch settings ── */
  const fetchSettings = useCallback(async () => {
    try {
      setSettingsLoading(true);
      const res = await axios.get("/api/settings/settings", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.data.success) {
        setTimeInterval(res.data.data.timeInterval);
        setSavedInterval(res.data.data.timeInterval);
        setUseGlobalInterval(res.data.data.useGlobalInterval);
      }
    } catch (err) {
      console.error("Failed to fetch settings:", err);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  /* ── Save settings ── */
  const saveSettings = async () => {
    try {
      setSettingsSaving(true);
      const res = await axios.put(
        "/api/settings/settings",
        { timeInterval, useGlobalInterval },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (res.data.success) {
        setSavedInterval(timeInterval);
        toast.success("Settings saved");
        setSettingsOpen(false);
      }
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSettingsSaving(false);
    }
  };

  /* ── Fetch available slots from server ──────────────────────────── */
  useEffect(() => {
    // Reset slots when not in approve mode or no record
    if (!selectedRecord || actionStatus !== "approved") {
      setAvailableSlots([]);
      setSelectedSlot(null);
      return;
    }

    // Need a date selected
    if (!scheduleDate) {
      setAvailableSlots([]);
      return;
    }

    const formattedDate = scheduleDate.format("YYYY-MM-DD");
    const doctorId = selectedRecord.doctorId; // ensure this exists in your appointment data

    if (!doctorId) {
      toast.error("Doctor ID missing for this appointment");
      setAvailableSlots([]);
      return;
    }

    setSlotsLoading(true);
    axios
      .get("/api/settings/time-slots", {
        params: { doctorId, date: formattedDate },
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
     .then(res => {
  if (res.data.success) {
    setAvailableSlots(res.data.data.availableSlots || []);
    setEffectiveInterval(res.data.data.intervalMinutes || 30);
  } else {
    toast.error(res.data.message || "Failed to load slots");
    setAvailableSlots([]);
  }
})
      .catch(err => {
        console.error("Slot fetch error:", err);
        toast.error("Could not load time slots");
        setAvailableSlots([]);
      })
      .finally(() => setSlotsLoading(false));
  }, [selectedRecord, actionStatus, scheduleDate]); // re-fetch when date changes

  /* ── Exports ── */
  const exportToCSV = () => {
    const data = filteredData.map(a => ({
      Patient:            a.userInfo?.name || "N/A",
      "Appointment Service": a.doctorInfo?.service || "N/A",
      "Doctor Name":      `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`,
      "Clinic Day":       a.doctorInfo?.clinicDay?.join(", ") || "N/A",
      "Scheduled Date":   a.schedule_date ? moment(a.schedule_date).format("DD-MM-YYYY") : "Not set",
      "Scheduled Time":   a.schedule_time ? fmt12(a.schedule_time) : "Not set",
      Status:             a.status,
      "Progress (%)":     getStatusProgress(a.status),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Appointments");
    const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(new Blob([out], { type: "application/octet-stream" }), "appointments.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["Patient", "Service", "Doctor", "Clinic Day", "Date", "Time", "Status", "%"]],
      body: filteredData.map(a => [
        a.userInfo?.name || "N/A",
        a.doctorInfo?.service || "N/A",
        `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`,
        a.doctorInfo?.clinicDay?.join(", ") || "N/A",
        a.schedule_date ? moment(a.schedule_date).format("DD-MM-YYYY") : "Not set",
        a.schedule_time ? fmt12(a.schedule_time) : "Not set",
        a.status,
        `${getStatusProgress(a.status)}%`,
      ]),
    });
    doc.save("appointments.pdf");
  };

  /* ── Table helpers ── */
  const handleTableChange = (_, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const openModal = (record, status) => {
    setSelectedRecord(record);
    setActionStatus(status);
    setSelectedSlot(null);
    form.resetFields();
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setUseCustomDate(false);
    setSelectedSlot(null);
    form.resetFields();
  };

  /* ── Submit approve/reject ── */
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const { comment, schedule_date } = values;

      if (actionStatus === "approved") {
        if (!schedule_date) { toast.error("Please pick a schedule date"); return; }
        if (!selectedSlot)  { toast.error("Please select a time slot");   return; }
      }

      setConfirmLoading(true);
      dispatch(showLoading());

      await axios.post(
        "/api/doctor/change-appointment-status",
        {
          appointmentId: selectedRecord._id,
          status:        actionStatus,
          comment,
          schedule_date:  schedule_date ? schedule_date.format("YYYY-MM-DD") : undefined,
          schedule_time:  selectedSlot || undefined, // send "HH:mm"
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      dispatch(hideLoading());
      toast.success(`Appointment ${actionStatus}`);
      closeModal();
      setConfirmLoading(false);
      getAppointmentData();
    } catch (error) {
  dispatch(hideLoading());

  toast.error(
    error?.response?.data?.message || "Something went wrong"
  );

  setConfirmLoading(false);
}
  };

  /* ── Clinic day / date picker helpers ── */
  const filteredData = appointments.filter(a => {
    const matchesSearch = a.userInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const clinicMatcher = useMemo(() => {
    const rules    = selectedRecord?.doctorInfo?.clinicDay || [];
    const matchers = rules.map(ruleToMatcher);
    return (dateMoment) => matchers.some(fn => fn(dateMoment));
  }, [selectedRecord]);

  const disableNonClinicDays = (current) =>
    !current || !current.isValid() ||
    current < moment().startOf("day") ||
    !clinicMatcher(current);

  const nextClinicDate = useMemo(() => {
    let d = moment().startOf("day");
    for (let i = 0; i < 30; i++) { if (clinicMatcher(d)) return d; d = d.clone().add(1, "day"); }
    return null;
  }, [clinicMatcher]);

  /* ── Preview slots for settings modal (unchanged) ── */
  const previewSlots = useMemo(() => {
    return generateSlots("08:00", "10:00", timeInterval).slice(0, 8);
  }, [timeInterval]);

  /* ── Counts ── */
  const counts = appointments.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  const isApprove = actionStatus === "approved";

  /* ── Columns ── */
  const columns = [
    {
      title: "Patient",
      key: "patient",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar size={32} style={{ background: `linear-gradient(135deg,${EMERALD},${TEAL})`, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {r.userInfo?.name?.[0]?.toUpperCase()}
          </Avatar>
          <Text strong style={{ fontSize: 13, color: SLATE }}>{r.userInfo?.name || "N/A"}</Text>
        </div>
      ),
      sorter: (a, b) => (a.userInfo?.name || "").localeCompare(b.userInfo?.name || ""),
      sortOrder: sortedInfo.columnKey === "patient" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Patient No.",
      key: "phone",
      render: (_, r) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.userInfo?.phone || "—"}</Text>,
      filters: [...new Set(appointments.map(a => a.userInfo?.phone))].filter(Boolean).map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.phone || null,
      onFilter: (value, record) => record.userInfo?.phone === value,
      ellipsis: true,
    },
    {
      title: "Service",
      key: "service",
      render: (_, r) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo?.service || "—"}</Text>,
      filters: [...new Set(appointments.map(a => a.doctorInfo?.service))].filter(Boolean).map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.service || null,
      onFilter: (value, record) => record.doctorInfo?.service === value,
      sorter: (a, b) => (a.doctorInfo?.service || "").localeCompare(b.doctorInfo?.service || ""),
      sortOrder: sortedInfo.columnKey === "service" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Doctor",
      key: "doctorName",
      render: (_, r) => (
        <Text style={{ fontSize: 13, color: SLATE_MID }}>
          Dr. {r.doctorInfo?.firstName || ""} {r.doctorInfo?.lastName || ""}
        </Text>
      ),
      sorter: (a, b) =>
        `${a.doctorInfo?.firstName} ${a.doctorInfo?.lastName}`.localeCompare(
          `${b.doctorInfo?.firstName} ${b.doctorInfo?.lastName}`
        ),
      sortOrder: sortedInfo.columnKey === "doctorName" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Location",
      key: "location",
      render: (_, r) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo?.location || "—"}</Text>,
      filters: [...new Set(appointments.map(a => a.doctorInfo?.location))].filter(Boolean).map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.location || null,
      onFilter: (value, record) => record.doctorInfo?.location === value,
      ellipsis: true,
    },
    {
      title: "Progress",
      key: "progress",
      render: (_, r) => {
        const cfg = getStatusCfg(r.status);
        return (
          <Tooltip title={`${cfg.progress}% — ${r.status}`}>
            <div style={{ minWidth: 90 }}>
              <Progress percent={cfg.progress} strokeColor={cfg.bar} size="small" showInfo={false} />
              <Text style={{ fontSize: 11, display: "block", textAlign: "center", marginTop: 2, color: cfg.bar, fontWeight: 700 }}>
                {cfg.progress}%
              </Text>
            </div>
          </Tooltip>
        );
      },
      sorter: (a, b) => getStatusProgress(a.status) - getStatusProgress(b.status),
      sortOrder: sortedInfo.columnKey === "progress" ? sortedInfo.order : null,
      width: 110,
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => {
        const cfg = getStatusCfg(r.status);
        return (
          <span className="aa-badge" style={{ background: cfg.bg, color: cfg.color }}>
            {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
          </span>
        );
      },
      width: 110,
    },
    {
      title: "Scheduled",
      key: "schedule_date",
      render: (_, r) =>
        (r.status === "approved" || r.status === "follow-up") && r.schedule_date ? (
          <div>
            <Text style={{ fontSize: 12, color: SLATE_MID, display: "block" }}>
              {moment(r.schedule_date).format("ddd, MMM D, YYYY")}
            </Text>
            {r.schedule_time && (
              <Text style={{ fontSize: 11, color: TEAL, fontWeight: 700 }}>
                <ClockCircleOutlined style={{ marginRight: 3 }} />
                {fmt12(r.schedule_time)}
              </Text>
            )}
          </div>
        ) : (
          <Text style={{ fontSize: 12, color: MUTED }}>—</Text>
        ),
      sorter: (a, b) => moment(a.schedule_date || 0).unix() - moment(b.schedule_date || 0).unix(),
      sortOrder: sortedInfo.columnKey === "schedule_date" ? sortedInfo.order : null,
    },
    {
      title: "Follow-Up",
      key: "followUpDate",
      render: (_, r) =>
        r.status === "follow-up" && r.followUpDate
          ? <Text style={{ fontSize: 12, color: PURPLE }}>{moment(r.followUpDate).format("ddd, MMM D, YYYY")}</Text>
          : <Text style={{ fontSize: 12, color: MUTED }}>—</Text>,
      sorter: (a, b) => moment(a.followUpDate || 0).unix() - moment(b.followUpDate || 0).unix(),
      sortOrder: sortedInfo.columnKey === "followUpDate" ? sortedInfo.order : null,
    },
    {
      title: "Actions",
      key: "action",
      width: 180,
      render: (_, r) =>
        r.status === "pending" ? (
          <Space>
            <Button className="aa-approve-btn" icon={<CheckCircleOutlined />} onClick={() => openModal(r, "approved")}>
              Approve
            </Button>
            <Button className="aa-reject-btn" icon={<CloseCircleOutlined />} onClick={() => openModal(r, "rejected")}>
              Reject
            </Button>
          </Space>
        ) : null,
    },
  ];

  /* ──────────────────────────────────────────────────────────── */
  return (
    <Layout>
      <div className="aa-root">
        <div className="aa-wrap">

          {/* ── Hero ── */}
          <div className="aa-hero aa-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="aa-eyebrow"><CalendarOutlined style={{ marginRight: 5 }} />Administration</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora',sans-serif", color: WHITE, fontWeight: 800 }}>
                Appointments
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Manage, approve, and track all patient appointment requests
              </Text>
              <div className="aa-hero-badges">
                <div className="aa-hero-badge"><CalendarOutlined style={{ marginRight: 5 }} />{appointments.length} Total</div>
                {counts.pending   && <div className="aa-hero-badge" style={{ background: "#fef9c3cc", color: "#b45309" }}>⏳ {counts.pending} Pending</div>}
                {counts.approved  && <div className="aa-hero-badge" style={{ background: `${TEAL_LIGHT}cc`, color: EMERALD }}>✓ {counts.approved} Approved</div>}
                {counts.completed && <div className="aa-hero-badge" style={{ background: "#dcfce7cc", color: "#16a34a" }}>✔ {counts.completed} Completed</div>}
                <div className="aa-hero-badge" style={{ background: `${PURPLE_LIGHT}cc`, color: PURPLE }}>
                  <ClockCircleOutlined style={{ marginRight: 4 }} />{displayInterval}min intervals
                </div>
              </div>
            </div>
          </div>

          {/* ── Toolbar ── */}
          <div className="aa-toolbar aa-fade aa-fade-1">
            <Input
              className="aa-search"
              prefix={<SearchOutlined style={{ color: MUTED }} />}
              placeholder="Search patient..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              allowClear
            />
            <Select
              className="aa-status-select"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 160 }}
              options={[
                { value: "All",        label: "All Statuses" },
                { value: "pending",    label: "Pending"      },
                { value: "approved",   label: "Approved"     },
                { value: "rejected",   label: "Rejected"     },
                { value: "follow-up",  label: "Follow-up"    },
                { value: "completed",  label: "Completed"    },
              ]}
            />
            <Button className="aa-btn aa-btn-excel" icon={<FileExcelOutlined />} onClick={exportToCSV}>Excel</Button>
            <Button className="aa-btn aa-btn-pdf"   icon={<FilePdfOutlined />}   onClick={exportToPDF}>PDF</Button>
            <Button className="aa-btn" icon={<FilterOutlined />} onClick={() => setFilteredInfo({})}>Filters</Button>
            <Button className="aa-btn" icon={<ClearOutlined />}  onClick={() => { setFilteredInfo({}); setSortedInfo({}); }}>Reset</Button>
            <Button
              className="aa-btn aa-btn-settings"
              icon={<SettingOutlined />}
              onClick={() => { setTimeInterval(savedInterval); setSettingsOpen(true); }}
            >
              Settings
            </Button>
          </div>

          {/* ── Table ── */}
          <div className="aa-table-wrap aa-fade aa-fade-2">
            <Table
              columns={columns}
              dataSource={filteredData.map(a => ({ ...a, key: a._id }))}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
              pagination={{ pageSize: 10, showSizeChanger: false }}
            />
          </div>

          {/* ══════════════════════════════════════════════════════ */}
          {/* ── Approve / Reject Modal ── */}
          {/* ══════════════════════════════════════════════════════ */}
          <Modal
            className={`aa-modal ${isApprove ? "approve-modal" : "reject-modal"}`}
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: isApprove ? TEAL_LIGHT : "#fee2e2",
                  color: isApprove ? EMERALD : "#dc2626",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {isApprove ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                </div>
                <span>{isApprove ? "Approve Appointment" : "Reject Appointment"}</span>
              </div>
            }
            open={open}
            onCancel={closeModal}
            footer={[
              <Button key="cancel" className="aa-modal-cancel" onClick={closeModal}>
                Cancel
              </Button>,
              <Button
                key="ok"
                className={isApprove ? "aa-modal-approve" : "aa-modal-reject"}
                loading={confirmLoading}
                onClick={handleOk}
                icon={isApprove ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              >
                {isApprove ? "Approve" : "Reject"}
              </Button>,
            ]}
            width={580}
            destroyOnClose
          >
            <Form form={form} layout="vertical" requiredMark={false}>

              {/* Comment */}
              <Form.Item
                label="Comment"
                name="comment"
                rules={[{ required: true, message: "Please add a comment" }]}
              >
                <Input.TextArea rows={3} placeholder="Add a comment or reason..." />
              </Form.Item>

              {/* ── Approve-only fields ── */}
              {isApprove && (
                <>
                  {/* Date mode toggle */}
                  <div className="aa-date-mode-strip">
                    <div>
                      <div className="aa-date-mode-label">Clinic days only</div>
                      <div className="aa-date-mode-sub">Respect doctor's available days</div>
                    </div>
                    <Switch
                      checked={useCustomDate}
                      onChange={setUseCustomDate}
                      checkedChildren="Custom"
                      unCheckedChildren="Clinic"
                      style={{ background: useCustomDate ? "#f97316" : TEAL }}
                    />
                    <div style={{ textAlign: "right" }}>
                      <div className="aa-date-mode-label">Custom date</div>
                      <div className="aa-date-mode-sub">Override clinic days</div>
                    </div>
                  </div>

                  {/* Date picker */}
                  <Form.Item
                    label={
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        Schedule Date
                        <span style={{
                          padding: "1px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                          background: useCustomDate ? "#fff7ed" : TEAL_LIGHT,
                          color: useCustomDate ? "#f97316" : EMERALD,
                        }}>
                          {useCustomDate ? "Custom" : "Clinic Days"}
                        </span>
                      </span>
                    }
                    name="schedule_date"
                    rules={[{ required: true, message: "Please choose a schedule date" }]}
                  >
                    <DatePicker
                      disabledDate={useCustomDate ? undefined : disableNonClinicDays}
                      placeholder={
                        useCustomDate
                          ? "Select any date"
                          : `Clinic days: ${selectedRecord?.doctorInfo?.clinicDay?.join(", ")}`
                      }
                      format="dddd, MMMM Do, YYYY"
                      suffixIcon={<CalendarOutlined style={{ color: MUTED }} />}
                    />
                  </Form.Item>

                  {/* Date alerts */}
                  {!useCustomDate && nextClinicDate && (
                    <Alert
                      message={<span>Next clinic day: <Text strong>{nextClinicDate.format("dddd, MMMM D, YYYY")}</Text></span>}
                      type="info" showIcon icon={<InfoCircleOutlined />}
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  {useCustomDate && (
                    <Alert
                      message="Overriding clinic days. Ensure the doctor is available on this date."
                      type="warning" showIcon icon={<WarningOutlined />}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  <Divider style={{ margin: "4px 0 16px" }} />

                  {/* ── Time Slot Picker ── */}
                  <Form.Item
                    label={
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <ClockCircleOutlined style={{ color: TEAL }} />
                        Schedule Time
                        <span style={{
                          padding: "1px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                          background: TEAL_LIGHT, color: EMERALD,
                        }}>
                         {displayInterval}min intervals
                        </span>
                        {selectedRecord?.doctorInfo?.appointmentTime && (
                          <span style={{ fontSize: 11, color: MUTED }}>
                            ({selectedRecord.doctorInfo.appointmentTime[0]} – {selectedRecord.doctorInfo.appointmentTime[1]})
                          </span>
                        )}
                      </span>
                    }
                    required
                    validateStatus={!selectedSlot && confirmLoading ? "error" : ""}
                  >
                    {slotsLoading ? (
                      <div className="aa-slot-loading"><Spin size="small" /><Text style={{ marginLeft: 8, color: MUTED }}>Loading slots…</Text></div>
                    ) : availableSlots.length === 0 ? (
                      <Alert
                        message="No available time slots for this doctor on the selected date."
                        type="warning" showIcon
                      />
                    ) : (
                      <>
                        <div className="aa-slot-grid">
                          {availableSlots.map(slot => (
                            <button
                              key={slot}
                              type="button"
                              className={`aa-slot-btn${selectedSlot === slot ? " active" : ""}`}
                              onClick={() => setSelectedSlot(slot === selectedSlot ? null : slot)}
                            >
                              {fmt12(slot)}
                            </button>
                          ))}
                        </div>
                        {!selectedSlot && (
                          <Text style={{ fontSize: 11, color: "#f97316", marginTop: 6, display: "block" }}>
                            ↑ Please select a time slot
                          </Text>
                        )}
                        {selectedSlot && (
                          <Text style={{ fontSize: 11, color: EMERALD, marginTop: 6, display: "block", fontWeight: 700 }}>
                            ✓ Selected: {fmt12(selectedSlot)}
                          </Text>
                        )}
                      </>
                    )}
                  </Form.Item>
                </>
              )}
            </Form>
          </Modal>

          {/* ══════════════════════════════════════════════════════ */}
          {/* ── Settings Modal ── */}
          {/* ══════════════════════════════════════════════════════ */}
          <Modal
            className="aa-settings-modal"
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <SettingOutlined style={{ fontSize: 18, color: WHITE }} />
                <span>Appointment Settings</span>
              </div>
            }
            open={settingsOpen}
            onCancel={() => setSettingsOpen(false)}
            footer={[
              <Button key="cancel" className="aa-modal-cancel" onClick={() => setSettingsOpen(false)}>
                Cancel
              </Button>,
              <Button
                key="save"
                className="aa-modal-save-settings"
                loading={settingsSaving}
                onClick={saveSettings}
                icon={<ThunderboltOutlined />}
              >
                Save Settings
              </Button>,
            ]}
            width={480}
            destroyOnClose
          >
            {settingsLoading ? (
              <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
            ) : (
              <>
                {/* ── NEW: Global interval toggle ── */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <Text strong style={{ fontSize: 14, color: SLATE, display: "block" }}>
                        Enable global time interval
                      </Text>
                      <Text style={{ fontSize: 12, color: MUTED }}>
                        When OFF, a default 30‑minute interval is used.
                      </Text>
                    </div>
                    <Switch
                      checked={useGlobalInterval}
                      onChange={setUseGlobalInterval}
                      checkedChildren="On"
                      unCheckedChildren="Off"
                      style={{ background: useGlobalInterval ? TEAL : "#d9d9d9" }}
                    />
                  </div>
                </div>

                <div className="aa-interval-card">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div>
                      <Text strong style={{ fontSize: 14, color: SLATE, display: "block" }}>
                        Time Slot Interval
                      </Text>
                      <Text style={{ fontSize: 12, color: MUTED }}>
                        Minutes between each appointment slot
                      </Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <InputNumber
                        min={5} max={120} step={5}
                        value={timeInterval}
                        onChange={val => setTimeInterval(val || 30)}
                        disabled={!useGlobalInterval}
                        style={{ width: 80, borderRadius: 8, borderColor: TEAL }}
                      />
                      <Text style={{ color: MUTED, fontSize: 12 }}>min</Text>
                    </div>
                  </div>

                  {!useGlobalInterval && (
                    <Text style={{ fontSize: 12, color: "#f97316", display: "block", marginBottom: 8 }}>
                      ⚠️ Global interval is disabled – default 30 min will be used.
                    </Text>
                  )}

                  {/* Quick-select presets */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                    {[10, 15, 20, 30, 45, 60].map(v => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setTimeInterval(v)}
                        disabled={!useGlobalInterval}
                        style={{
                          padding: "4px 12px", borderRadius: 20, border: "1.5px solid",
                          borderColor: timeInterval === v ? TEAL : "#e2e8f0",
                          background:  timeInterval === v ? TEAL_LIGHT : WHITE,
                          color:       timeInterval === v ? EMERALD : SLATE_MID,
                          fontSize: 12, fontWeight: 700, cursor: useGlobalInterval ? "pointer" : "not-allowed",
                          transition: "all .15s", opacity: useGlobalInterval ? 1 : 0.5,
                        }}
                      >
                        {v}min
                      </button>
                    ))}
                  </div>

                  {/* Slot preview */}
                  <Text style={{ fontSize: 11, color: MUTED, display: "block", marginBottom: 6 }}>
                    Preview (08:00 – 10:00 example):
                  </Text>
                  <div className="aa-interval-preview">
                    {previewSlots.map(s => (
                      <span key={s} className="aa-interval-pill">{fmt12(s)}</span>
                    ))}
                    {generateSlots("08:00", "10:00", timeInterval).length > 8 && (
                      <span className="aa-interval-pill" style={{ background: "#f1f5f9", color: MUTED }}>
                        +{generateSlots("08:00", "10:00", timeInterval).length - 8} more
                      </span>
                    )}
                  </div>
                </div>

                <Alert
                  style={{ marginTop: 16, borderRadius: 10 }}
                  type="info"
                  showIcon
                  message={
                    <Text style={{ fontSize: 12 }}>
                      {useGlobalInterval
                        ? `This interval applies to all doctors. Each doctor's available slots are generated from their own appointment time range.`
                        : `Global interval is disabled. A fixed 30‑minute interval will be used for all doctors.`}
                    </Text>
                  }
                />
              </>
            )}
          </Modal>

        </div>
      </div>
    </Layout>
  );
};

export default AppointmentAdmin;